import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/context.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import type { GeneratedLibrary } from '../hooks/useGeneratedLibrary.js';
import { mediaResponseToFile, type LibraryItem, type LibraryKind } from '../lib/generatedLibrary.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';

export interface LibraryPickerModalProps {
  open: boolean;
  onClose: () => void;
  library: GeneratedLibrary;
  /** Narrow the grid to what the current slot accepts (defaults to everything loaded). */
  kinds?: readonly LibraryKind[];
  /** How many results may be chosen at once (the section's remaining capacity). */
  maxSelect: number;
  /** Receives the chosen results as ordinary files, exactly like a file picker would. */
  onPick: (files: File[]) => void | Promise<void>;
}

function Thumb({ item }: { item: LibraryItem }) {
  if (item.kind === 'image') {
    return <img src={item.url} alt={item.prompt} loading="lazy" className="lib-thumb" />;
  }
  if (item.kind === 'video') {
    return <video src={item.url} preload="metadata" muted playsInline className="lib-thumb" />;
  }
  return (
    <span className="lib-thumb lib-thumb--audio">
      <Icon name="waveform" size={28} />
    </span>
  );
}

/** Pick previously generated content (any section) to reuse as a reference or attachment. */
export function LibraryPickerModal({ open, onClose, library, kinds, maxSelect, onPick }: LibraryPickerModalProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const { toast } = useUI();
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { refresh } = library;

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    void refresh();
  }, [open, refresh]);

  const limit = Math.max(1, maxSelect);
  const items = kinds ? library.items.filter((item) => kinds.includes(item.kind)) : library.items;

  function toggle(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      // A single-file slot swaps the choice instead of getting stuck at the limit.
      if (limit === 1) return [id];
      return prev.length >= limit ? prev : [...prev, id];
    });
  }

  async function confirm() {
    const chosen = selected
      .map((id) => items.find((item) => item.id === id))
      .filter((item): item is LibraryItem => Boolean(item));
    if (chosen.length === 0 || busy) return;
    setBusy(true);
    try {
      const files = await Promise.all(
        chosen.map(async (item) => mediaResponseToFile(await api.getJobMedia(item.jobId, item.index))),
      );
      await onPick(files);
      onClose();
    } catch {
      toast(t('library.loadFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('library.title')}
      closeLabel={t('common.close')}
      className="lib-modal"
      footer={
        <button
          type="button"
          className="btn btn-primary lib-confirm"
          disabled={selected.length === 0 || busy}
          onClick={() => void confirm()}
        >
          {busy
            ? t('library.loading')
            : t('library.attach', { count: String(selected.length), max: String(limit) })}
        </button>
      }
    >
      {items.length === 0 ? (
        <p className="lib-empty">{library.loading ? t('library.loading') : t('library.empty')}</p>
      ) : (
        <>
          <ul className="lib-grid" role="listbox" aria-multiselectable={limit > 1}>
            {items.map((item) => {
              const order = selected.indexOf(item.id);
              const isSelected = order !== -1;
              return (
                <li key={item.id} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={`lib-cell${isSelected ? ' is-selected' : ''}`}
                    title={item.prompt || item.modelName}
                    aria-label={item.prompt || item.modelName}
                    onClick={() => toggle(item.id)}
                  >
                    <Thumb item={item} />
                    {item.kind === 'video' && (
                      <span className="lib-kind" aria-hidden="true">
                        <Icon name="video" size={12} />
                      </span>
                    )}
                    {isSelected && <span className="lib-check">{limit > 1 ? order + 1 : '✓'}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {library.hasMore && (
            <button
              type="button"
              className="btn btn-ghost lib-more"
              disabled={library.loading}
              onClick={() => void library.loadMore()}
            >
              {t('library.loadMore')}
            </button>
          )}
        </>
      )}
    </Modal>
  );
}
